import { Box, Container, Typography, Card, CardContent, Link, Stack } from '@mui/material'
import { Email, Language, Phone, Schedule, BugReport, HelpOutline } from '@mui/icons-material'

export default function SupportPage() {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: 6 }}>
      <Container maxWidth="md">
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box component="img" src="/omnitrace_logo.png" alt="OmniTrace" sx={{ width: 36, height: 36 }} />
          <Typography variant="h5" fontWeight={800} color="#0f172a">OmniTrace</Typography>
        </Box>

        <Typography variant="h4" fontWeight={800} color="#0f172a" gutterBottom>
          Support
        </Typography>
        <Typography variant="body1" color="#64748b" sx={{ mb: 4 }}>
          Need help with OmniTrace? We're here to assist you with setup, troubleshooting,
          and any questions about our GPS fleet tracking platform.
        </Typography>

        <Stack spacing={2} sx={{ mb: 5 }}>
          <SupportCard
            icon={<Email sx={{ color: '#34A8E0' }} />}
            title="Email Support"
            description="For general inquiries and technical support"
            action={<Link href="mailto:support@omnitrace.app" fontWeight={600}>support@omnitrace.app</Link>}
          />
          <SupportCard
            icon={<Phone sx={{ color: '#10b981' }} />}
            title="Phone Support"
            description="Available Sunday–Thursday, 9 AM – 6 PM (Baghdad time)"
            action={<Link href="tel:+9647700000000" fontWeight={600}>+964 770 000 0000</Link>}
          />
          <SupportCard
            icon={<Language sx={{ color: '#8b5cf6' }} />}
            title="Website"
            description="Visit our website for documentation and updates"
            action={<Link href="https://omnitrace.app" target="_blank" fontWeight={600}>omnitrace.app</Link>}
          />
          <SupportCard
            icon={<Schedule sx={{ color: '#f59e0b' }} />}
            title="Response Time"
            description="We aim to respond to all support requests within 24 hours during business days"
            action={null}
          />
        </Stack>

        <Typography variant="h5" fontWeight={700} color="#1e293b" sx={{ mb: 2 }}>
          Frequently Asked Questions
        </Typography>

        <Stack spacing={2} sx={{ mb: 5 }}>
          <FaqCard
            question="How do I add a new device?"
            answer="Go to Devices → All Devices → Register Device. Enter the device IMEI/serial number, select the device type and vehicle type, then click Register."
          />
          <FaqCard
            question="How do I assign a device to a driver?"
            answer="Go to Devices → Device Assignments. Select the user, then check the devices you want them to see. You can also assign devices via Device Groups for role-based access."
          />
          <FaqCard
            question="Why is my device showing as offline?"
            answer="A device shows as offline when it hasn't sent data within the configured timeout (default: 5 minutes). Check that the device has power, a valid SIM card with data, and is within cellular coverage."
          />
          <FaqCard
            question="How do I set up push notifications?"
            answer="Push notifications are enabled automatically when you install the mobile app and allow notifications. Alerts from rules and geofences will be delivered based on your notification settings."
          />
          <FaqCard
            question="Can I share live tracking with a customer?"
            answer="Yes. Go to the device detail page and click 'Share'. This generates a temporary tracking link that you can send to anyone — no login required."
          />
          <FaqCard
            question="How do I export reports?"
            answer="Go to Reports → select a template or create a custom report → click Generate. Reports are downloaded as CSV files. You can also schedule automatic email delivery."
          />
        </Stack>

        <Card sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <BugReport sx={{ fontSize: 40, color: '#94a3b8', mb: 1 }} />
            <Typography variant="h6" fontWeight={700} color="#1e293b">
              Found a bug?
            </Typography>
            <Typography variant="body2" color="#64748b" sx={{ mt: 1 }}>
              Report issues to{' '}
              <Link href="mailto:bugs@omnitrace.app">bugs@omnitrace.app</Link>
              {' '}with a description and screenshots.
            </Typography>
          </CardContent>
        </Card>

        <Typography variant="body2" color="#94a3b8" sx={{ mt: 6, textAlign: 'center' }}>
          © {new Date().getFullYear()} OmniTrace. All rights reserved.
        </Typography>
      </Container>
    </Box>
  )
}

function SupportCard({ icon, title, description, action }: {
  icon: React.ReactNode; title: string; description: string; action: React.ReactNode | null
}) {
  return (
    <Card sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {icon}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={700} color="#1e293b">{title}</Typography>
          <Typography variant="body2" color="#64748b">{description}</Typography>
        </Box>
        {action}
      </CardContent>
    </Card>
  )
}

function FaqCard({ question, answer }: { question: string; answer: string }) {
  return (
    <Card sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <HelpOutline sx={{ color: '#34A8E0', mt: 0.3, fontSize: 20 }} />
          <Box>
            <Typography variant="subtitle2" fontWeight={700} color="#1e293b">{question}</Typography>
            <Typography variant="body2" color="#64748b" sx={{ mt: 0.5 }}>{answer}</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}
